import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import MetazikIcon from "@/components/MetazikIcon";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Le mot de passe doit contenir au moins 6 caractères.");
    if (pw !== pw2) return setErr("Les deux mots de passe ne sont pas identiques.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e2) {
      setErr(!e2.response ? "Le serveur met du temps à répondre. Réessaie dans quelques secondes." : formatApiError(e2.response.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-4 bg-[#050505]" data-testid="reset-page">
      <div className="w-full max-w-md bg-zinc-950/80 border border-white/10 rounded-3xl p-6 sm:p-8">
        <Link to="/auth" className="flex items-center gap-2 mb-6">
          <MetazikIcon className="w-9 h-9" />
          <span className="text-2xl font-black font-display text-white">Metazik</span>
        </Link>
        <h1 className="text-2xl font-black font-display text-white">Nouveau mot de passe</h1>

        {done ? (
          <div className="mt-5 space-y-4" data-testid="reset-done">
            <p className="text-sm text-zinc-300 flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> Ton mot de passe a été changé. Tu peux te connecter.</p>
            <Link to="/auth" className="btn-primary w-full inline-flex justify-center" data-testid="reset-to-login">Se connecter</Link>
          </div>
        ) : !token ? (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-zinc-300">Ce lien est incomplet. Redemande un lien depuis la page « Mot de passe oublié ».</p>
            <Link to="/forgot-password" className="btn-primary w-full inline-flex justify-center">Mot de passe oublié</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nouveau mot de passe" required minLength={6} dataTestid="reset-password" ariaShow="Afficher" ariaHide="Masquer" />
            <PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Répète le mot de passe" required minLength={6} dataTestid="reset-password2" ariaShow="Afficher" ariaHide="Masquer" />
            {err && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2" data-testid="reset-error">{err}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Changer mon mot de passe
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
